import React, { useState } from 'react';
import { applyAiChanges } from '../../services/api';
import { useToast } from '../../hooks/useToast';

function DiffBlock({ diff }) {
  if (!diff.diff) return null;
  const lines = diff.diff.split('\n');
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#818cf8', marginBottom: 4, fontFamily: 'monospace' }}>
        📄 {diff.file}
        <span style={{ marginLeft: 8, color: '#10b981' }}>+{diff.lines_added}</span>
        <span style={{ marginLeft: 4, color: '#ef4444' }}>-{diff.lines_removed}</span>
      </div>
      <div style={{ background: '#0f172a', borderRadius: 6, padding: '8px 10px', maxHeight: 200, overflowY: 'auto', fontSize: 11, fontFamily: 'monospace', lineHeight: 1.6 }}>
        {lines.slice(0, 60).map((line, i) => (
          <div key={i} style={{
            color: line.startsWith('+') && !line.startsWith('+++') ? '#10b981'
              : line.startsWith('-') && !line.startsWith('---') ? '#ef4444'
              : line.startsWith('@@') ? '#f59e0b'
              : '#94a3b8',
            whiteSpace: 'pre',
          }}>
            {line}
          </div>
        ))}
        {lines.length > 60 && <div style={{ color: '#475569' }}>... {lines.length - 60} more lines</div>}
      </div>
    </div>
  );
}

export default function AIDiffReview({ diff, projectId, onApplied, onDismiss }) {
  const [applying, setApplying] = useState(false);
  const toast = useToast();

  if (!diff) return null;

  const handleApply = async () => {
    setApplying(true);
    try {
      // Reconstruct files dict from diff info — actual content comes from the job
      await applyAiChanges(projectId, diff.job_id, Object.fromEntries(
        (diff.files || []).map(f => [f, '']) // backend re-reads from job result
      ));
      toast.success('AI changes applied to shared project');
      onApplied?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div style={s.overlay} onClick={onDismiss}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.header}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>🤖 AI Changes Ready for Review</span>
          <button onClick={onDismiss} style={s.closeBtn}>✕</button>
        </div>

        {/* Conflict warning */}
        {diff.has_conflicts && (
          <div style={s.conflictBanner}>
            ⚠️ <strong>Conflict detected.</strong> The following files are currently being edited:
            {diff.conflicts.map(c => (
              <div key={c.file} style={{ marginTop: 4, fontSize: 12 }}>
                <strong style={{ color: '#f59e0b' }}>{c.file}</strong> — being edited by <strong>{c.editing_user_email}</strong>
              </div>
            ))}
            <p style={{ margin: '8px 0 0', fontSize: 12 }}>Review carefully before applying.</p>
          </div>
        )}

        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 16px' }}>
          {diff.files?.length} file(s) will be modified. Review the changes below before applying to the shared project.
        </p>

        <div style={{ maxHeight: 360, overflowY: 'auto', marginBottom: 16 }}>
          {(diff.diffs || []).map((d, i) => <DiffBlock key={i} diff={d} />)}
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={handleApply} disabled={applying} style={s.applyBtn}>
            {applying ? 'Applying...' : '✓ Apply to Shared Project'}
          </button>
          <button onClick={onDismiss} style={s.dismissBtn}>Dismiss</button>
        </div>
      </div>
    </div>
  );
}

const s = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: 'var(--bg-card)', borderRadius: 12, padding: 24, width: '90%', maxWidth: 620, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid var(--border)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  closeBtn: { background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 16, cursor: 'pointer' },
  conflictBanner: { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#fca5a5' },
  applyBtn: { flex: 1, padding: '10px 16px', background: 'linear-gradient(135deg,#10b981,#059669)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  dismissBtn: { padding: '10px 16px', background: 'none', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' },
};
